<template>
    <div class="dynamic-form-page">
        <!-- Loading/Submitting full-page overlay -->
        <div v-if="loading || submitting" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; display: flex; align-items: center; justify-content: center; z-index: 9999;">
            <q-inner-loading :showing="true" :label="submitting ? 'Submitting, please wait...' : `Please wait, ${responseId ? 'retrieving past answers...' : 'loading form...'}`" label-class="text-teal"
                label-style="font-size: 1.1em" />
        </div>
        
        <!-- Form content -->
        <div v-if="structure && pagesList.length">
            <!-- Form header -->
            <h4 class="q-mt-xs">
                <i v-if="!standaloneMode" :style="{ cursor: 'pointer' }">
                    <q-icon name="chevron_left" @click="handleExit" />
                </i>
                {{ structure.title ?? 'Client Form' }}
                <q-badge v-if="structure.familyId" class="q-ml-sm">Version {{ structure.versionNumber }}</q-badge>
            </h4>
            
            <!-- Response subject name & metadata -->
            <div v-if="!publicMode" class="q-mb-md">
                <h6 class="form-subtitle q-mb-xs">
                    {{ subjectName }}
                    <q-badge v-if="isTemporaryClient" color="secondary" outline class="q-ml-sm text-weight-medium tk-client-badge">
                        Te Kohinga Person
                    </q-badge>
                    <q-btn v-if="!standaloneMode" size="16px" icon="o_history" no-caps flat outlined class="text-body2 q-ml-sm" color="grey-7"
                        @click="showHistoricResponses = true">&nbsp; View Past Responses</q-btn>
                </h6>
                <div v-if="isTemporaryClient && !externalMode" class="q-mb-sm">
                    <div v-if="subject.phone"><strong>Phone:</strong> {{ subject.phone }}</div>
                    <div v-if="subject.email"><strong>Email:</strong> {{ subject.email }}</div>
                </div>


                <div class="column items-left q-gutter-sm">
                    <span v-if="responseMetadata?.started_at" class="text-body2 text-grey-7">
                        Started: {{ formatDate(responseMetadata.started_at) }}
                    </span>
                    <span v-if="responseMetadata?.updated_at" class="text-body2 text-grey-7">
                        Updated: {{ formatDate(responseMetadata.updated_at) }}
                    </span>
                    <span v-if="responseMetadata?.completed_on" class="text-body2 text-grey-7">
                        Submitted: {{ formatDate(responseMetadata.completed_on) }}
                    </span>
                    <q-chip
                        v-if="responseMetadata?.status"
                        :color="responseMetadata.status === 'awaiting_approval' ? 'warning' : (responseMetadata.completed_on ? 'positive' : 'grey')"
                        text-color="white"
                    >
                        <strong>Status:&nbsp;</strong> {{ responseStatusText }}
                    </q-chip>
                </div>
            </div>

            <!-- Read-only banner for completed forms -->
            <q-banner v-if="isReadOnly && !standaloneMode" class="bg-grey-9 text-white q-mb-md" inline-actions>
                <template v-slot:avatar>
                    <q-icon name="lock" />
                </template>
                {{ structure.requiresApproval ? 'Reopening this assessment removes its approval. It will need to be approved again after editing.' : 'This form has been completed. To edit it, first mark the form as incomplete.' }}
                <template v-slot:action>
                    <q-btn 
                        flat 
                        dense 
                        no-caps
                        label="Mark as Incomplete" 
                        @click="reopenForm" 
                        :loading="reopening"
                    />
                </template>
            </q-banner>

            <!-- Error banner -->
            <q-banner v-if="formErrors.length > 0" class="bg-warning text-white q-mb-md" inline-actions>
                <template v-slot:avatar>
                    <q-icon name="error" />
                </template>
                Please fix the following errors before submitting:
                <ul class="q-my-sm">
                    <li v-for="(error, index) in formErrors" :key="index">{{ error }}</li>
                </ul>
                <template v-slot:action>
                    <q-btn flat dense icon="close" size="sm" @click="formErrors = []" />
                </template>
            </q-banner>

            <!-- Form pages -->
            <div v-for="(pageStructure, index) in pagesList" :key="index" v-show="index + 1 === page">
                <div class="q-my-md dynamic-form-page-content">
                    <!-- Page navigation header -->
                    <q-btn-dropdown
                        color="primary"
                        flat
                        no-caps
                        icon="menu"
                        :label="pageStructure.name || `Page ${index + 1}`"
                        class="q-ma-md text-body-2"
                    >
                        <q-item
                            v-for="(pageOption, pageIndex) in pagesList"
                            :key="pageIndex"
                            clickable
                            v-close-popup
                            @click="changePage(pageIndex + 1)"
                        >
                            <q-item-section>
                                <q-item-label>{{ pageOption.name || `Page ${pageIndex + 1}` }}</q-item-label>
                                <q-item-label caption>{{ pageOption?.description ?? '' }}</q-item-label>
                            </q-item-section>
                            <q-item-section side>
                                <q-icon v-if="page === pageIndex + 1" name="check" color="primary" />
                            </q-item-section>
                        </q-item>
                    </q-btn-dropdown>

                    <DynamicFormPage
                        :ref="`page-${index + 1}`"
                        :structure="pageStructure"
                        :response="response"
                        :approved-by-name="approvedByName"
                        :read-only="isReadOnly"
                        :show-opt-out="showOptOut"
                        @field-value="(fieldId, key, value) => setFieldValue(fieldId, key, value)"
                        @get-field-value="getFieldValue"
                    />
                    <!-- Submit button for final page only -->
                    <div class="row justify-center q-pb-xl q-gutter-sm">
                        <q-btn v-if="structure.requiresApproval && !standaloneMode && !isReadOnly" no-caps outline label="Save draft" @click="saveForm" />
                        <q-btn v-if="page === totalPages && structure.requiresApproval && structure.canApprove && !standaloneMode && responseMetadata?.status !== 'approved'"
                            no-caps color="primary" label="Mark as approved" :loading="submitting" @click="approveForm" />
                        <q-btn 
                            v-if="page === totalPages && !isReadOnly"
                            size="lg"
                            no-caps
                            style="min-width: 300px;"
                            unelevated
                            color="positive"
                            @click="completeForm"
                        >
                            {{ structure.requiresApproval ? 'Submit for approval' : 'Submit Form' }}
                        </q-btn>
                    </div>
                </div>

                <!-- Page navigation footer -->
                <div class="row justify-between q-my-md items-center">
                    <div class="text-grey-7">{{ !standaloneMode && autosaving ? "Auto-saving..." : "" }}</div>
                    <div class="q-gutter-md">
                        <q-btn
                            flat
                            no-caps
                            color="primary"
                            :style="{ display: page === 1 ? 'none' : 'inline' }"
                            @click="changePage(Math.max(1, page - 1))"
                        >
                            Previous Page
                        </q-btn>
                        <q-btn
                            outline
                            no-caps
                            color="primary"
                            :style="{ display: page === totalPages ? 'none' : 'inline' }"
                            @click="changePage(Math.min(totalPages, page + 1))"
                        >
                            Next Page
                        </q-btn>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Past responses modal navigator -->
    <q-dialog v-if="!standaloneMode" v-model="showHistoricResponses">
        <q-card style="min-width: 1500px; max-width: 800px;">
            <q-inner-loading :showing="loadingHistory" :label="'Loading response...'" label-class="text-teal"
                label-style="font-size: 1.1em" />

            <q-card-section class="q-pt-none q-ma-md">
                <q-tabs v-model="historyTab" dense align="left" class="text-teal" active-color="primary">
                    <q-tab name="table" label="Responses Table" no-caps />
                    <q-tab v-if="formId == 1 && !isTemporaryClient" name="legacy" label="Te Iti Kahurangi History" no-caps />
                </q-tabs>

                <q-separator />

                <q-tab-panels v-model="historyTab" animated class="scroll" style="max-height: 80vh;">
                    <q-tab-panel name="table">
                        <PreviousFormsTable
                            :subject="subject"
                            :selected-form-id="activeFormId"
                            :current-response-id="responseId"
                            @form-selected="loadPreviousResponse"
                            @exit-form="handleDeleteCurrentForm"
                        />
                    </q-tab-panel>

                    <q-tab-panel v-if="formId == 1 && !isTemporaryClient" name="legacy">
                        <FormHistoryResponse :client="{ client_id: subject.id }" formType="Te Iti Kahurangi" />
                    </q-tab-panel>
                </q-tab-panels>
            </q-card-section>
        </q-card>
    </q-dialog>

    <q-dialog v-model="externalSubmitDialog" persistent>
        <q-card style="min-width: 380px; max-width: 540px;">
            <q-card-section class="row items-center q-gutter-sm">
                <q-icon name="o_warning" color="warning" size="32px" />
                <div class="text-h6">Submit this form?</div>
            </q-card-section>
            <q-card-section class="text-body1">
                Once you submit, you will not be able to change your answers. Please check them before continuing.
            </q-card-section>
            <q-card-actions align="right">
                <q-btn flat no-caps label="Go back and review" color="primary" v-close-popup />
                <q-btn unelevated no-caps label="Submit final answers" color="positive" :loading="submitting" @click="confirmExternalSubmission" />
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>

<script>
    import api from "@/services/api/api";
    import notify from "@/services/util/notify";
    import DynamicFormPage from "./DynamicFormPage.vue";
    import PreviousFormsTable from "./PreviousFormsTable.vue";
    import FormHistoryResponse from "../DataComponents/Forms/FormHistoryResponseComponent.vue";
    import { subjectWithClientEngagement, subjectWithoutClientEngagement } from "@/services/util/formSubject";

    export default {
        name: "DynamicFormRenderer",
        components: {DynamicFormPage, PreviousFormsTable, FormHistoryResponse},
        props: {
            formId: {
                type: [Number, String],
                required: true,
            },
            // The form subject ({ source, id, name, email, phone }).
            subject: {
                type: Object,
                required: true,
            },
            _responseId: {
                type: [Number, String],
                default: null,
            },
            showOptOut: {
                type: Boolean,
                default: true,
            },
            initialStructure: {
                type: Object,
                default: null,
            },
            externalToken: {
                type: String,
                default: null,
            },
            publicMode: {
                type: Boolean,
                default: false,
            }
        },
        emits: ['exit', 'form-submitted', 'access-expired'],
        data() {
            return {
                activeFormId: this.formId,
                responseId: this._responseId,
                responseMetadata: null,
                loading: false,
                autosaving: false,
                AUTO_INTERVAL_MS: 30000,
                submitting: false,
                reopening: false,
                page: 1,
                response: {},
                structure: this.initialStructure,
                showHistoricResponses: false,
                formErrors: [],
                autoSaveInterval: null,
                formModified: false,
                loadingHistory: false,
                historyTab: 'table',
                externalSubmitDialog: false,
            };
        },
        async created() {
            this.loading = true;
            if (!this.structure) await this.fetchFormDefinition();
            if (this.responseId) {
                await this.fetchPastResponse();
            }
            this.loading = false;
            if (!this.standaloneMode) this.startAutoSave();
        },
        beforeUnmount() {
            this.stopAutoSave();
        },
        methods: {
            // Attempt an autosave before navigating away
            handleBeforeRouteLeave(next) {
                if (this.standaloneMode || this.activeFormId === null || this.subject === null) {
                    next();
                } else {
                    if (this.formModified && !this.isReadOnly) {
                        this.submitForm(false, true).then(() => {
                            next();
                        }).catch(() => {
                            next();
                        });
                    } else {
                        next();
                    }
                }
            },
            /**
             * Handles exit with attempted autosave.
             */
            async handleExit() {
                if (this.externalMode) return;
                if (this.publicMode) {
                    this.$emit('exit');
                    return;
                }
                if (this.formModified && !this.isReadOnly) {
                    try {
                        await this.submitForm(false, true);
                    } catch (e) {
                        console.warn('Autosave failed on exit:', e);
                    }
                }
                this.$emit('exit');
            },
            /**
             * Retrieve the form's structure information from the API
             */
            async fetchFormDefinition() {
                try {
                    if (this.externalMode) {
                        const response = await api.forms.getExternalFormInvitation(this.externalToken);
                        this.structure = response.data.form;
                    } else if (this.publicMode) {
                        const response = await api.publicKoTatou.getForm();
                        this.structure = response.data;
                    } else {
                        const response = await api.forms.getDynamicForm(this.activeFormId);
                        this.structure = response.data;
                    }
                } catch (error) {
                    console.error("Error fetching form data:", error);
                    if (this.publicMode) {
                        this.$emit(
                            'access-expired',
                            error.response?.data?.message || 'The form could not be loaded. Please enter the PIN again.'
                        );
                    }
                }
            },
            /**
             * Retrieve the past response data from the API
             */
            async fetchPastResponse(preserveAnswers = false) {
                try {
                    const response = await api.forms.getPastFormResponse(this.activeFormId, this.responseId);
                    this.responseMetadata = response.data.data[0];
                    if (!preserveAnswers) this.response = response.data.data[0].fields ?? {};
                } catch (error) {
                    console.error("Error fetching past response:", error);
                    this.$emit('exit');
                }
            },
            /**
             * Navigate to another page, saving the form's current progress and scrolling to top of page.
             * @param newPage page indices to navigate to (1-based)
             */
            async changePage(newPage) {
                if (!this.standaloneMode && !this.isReadOnly) {
                    this.submitForm(false, true);
                }
                this.page = newPage;
                window.scrollTo({ top: 0, behavior: 'smooth' });
            },
            /**
             * Initialise the periodic autosave callback.
             */
            startAutoSave() {
                if (this.standaloneMode) return;
                this.autoSaveInterval = setInterval(async () => {
                    if (Object.keys(this.response).length > 0 && !this.submitting && !this.isReadOnly) {
                        await this.submitForm(false, true);
                    }
                }, this.AUTO_INTERVAL_MS);
            },
            /**
             * Stop the periodic autosave callback.
             */
            stopAutoSave() {
                if (this.autoSaveInterval) {
                    clearInterval(this.autoSaveInterval);
                }
            },
            /**
             * Returns the value for a given field from the response object.
             * @param fieldId field ID to get value for
             */
            getFieldValue(fieldId) {
                return this.response[fieldId];
            },
            /**
             * Sets the value for a given field in the response object.
             * @param fieldId field ID to set value for
             * @param key key within the field object to set (default: 'value')
             * @param value value to set
             */
            setFieldValue(fieldId, key='value', value) {
                if (this.isReadOnly) return;
                
                this.formModified = true;
                
                if (key === null || key === undefined) {
                    this.response[fieldId] = { value };
                } else {
                    if (!this.response[fieldId] || typeof this.response[fieldId] !== 'object') {
                        this.response[fieldId] = {};
                    }
                    this.response[fieldId][key] = value;
                }
            },
            /**
             * Checks for all required fields across all pages and returns an array of error messages for any that are missing.
             * @return Array of error message strings for required fields that are missing.
             */
            validateRequiredFields() {
                const errors = [];
                
                const pages = this.pagesList;
                
                for (const page of pages) {
                    const fields = page.fields || [];
                    
                    for (const field of fields) {
                        // Skip display text fields
                        if (field.fieldType === 'DISPLAY_TEXT' || field.options?.approvalField) continue;
                        
                        if (field.required) {
                            const fieldData = this.response?.[field.id];
                            // Skip required check if client opted out of this field
                            if (fieldData?.opted_out === true) continue;
                            console.log('Validating required field:', field.id, 'Data:', fieldData);
                            if (fieldData == 0 && field.fieldType === 'STAR_RATING') {
                                continue; // 0 is valid for STAR_RATING
                            }

                            if (fieldData === undefined || fieldData === null) {
                                errors.push(`Field "${field.label || field.id}" is required.`);
                                continue;
                            }
                            const value = fieldData.value;
                            if (value === undefined || value === null || value === '') {
                                errors.push(`Field "${field.label || field.id}" is required.`);
                            }
                        }
                    }
                }
                
                return errors;
            },
            /**
             * Checks for validation errors across all pages by calling each page's validateAllFields() method.
             * @return Array of error message strings for fields that failed validation.
             */
            validateFormFields() {
                const errors = [];
                
                for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
                    const pageRefs = this.$refs[`page-${pageNum}`];
                    if (!pageRefs || !pageRefs.length) continue;
                    
                    const pageRef = Array.isArray(pageRefs) ? pageRefs[0] : pageRefs;
                    if (!pageRef) continue;
                    
                    if (pageRef.validateAllFields && typeof pageRef.validateAllFields === 'function') {
                        const fieldErrors = pageRef.validateAllFields();
                        if (fieldErrors && Object.keys(fieldErrors).length > 0) {
                            Object.values(fieldErrors).forEach(error => {
                                errors.push(error);
                            });
                        }
                    }
                }
                
                return errors;
            },
            /**
             * Saves the form mid-progress.
             */
            async approveForm() {
                if (this.submitting || this.autosaving) return;
                const errors = [...this.validateFormFields(), ...this.validateRequiredFields()];
                if (errors.length) { this.formErrors = errors; return; }
                if (!this.responseId || this.formModified) {
                    if (!await this.submitForm(false)) return;
                }
                this.submitting = true;
                try {
                    await api.forms.approveFormResponse(this.activeFormId, this.responseId, this.responseMetadata.revision);
                    await this.fetchPastResponse();
                    notify.primary('Assessment approved', 'top');
                    this.$emit('form-submitted');
                } catch (error) { notify.error(error.response?.data?.message || 'Unable to approve assessment', 'top'); }
                finally { this.submitting = false; }
            },
            async saveForm() {
                await this.submitForm(false);
            },
            /**
             * Checks if the form is valid, and if so, submits it as complete.
             */
            async completeForm() {
                const validationErrors = this.validateFormFields();
                const requiredFieldErrors = this.validateRequiredFields();
                const allErrors = [...validationErrors, ...requiredFieldErrors];
                if (allErrors.length > 0) {
                    this.formErrors = allErrors;
                    notify.error('Please fix the errors in the form before submitting.', 'top');
                    return;
                }
                if (this.externalMode) {
                    this.externalSubmitDialog = true;
                    return;
                }
                await this.submitForm(true);
            },
            async confirmExternalSubmission() {
                const submitted = await this.submitForm(true);
                if (submitted) this.externalSubmitDialog = false;
            },
            /**
             * Used by the past responses modal to navigate to another response.
             * @param selectedForm identifier of the selected past form response
             */
            async loadPreviousResponse(newResponseId, responseFormId) {
                this.loadingHistory = true;
                
                try {
                    // Save current form only if it has been modified
                    if (this.formModified && !this.isReadOnly) {
                        if (!await this.submitForm(false)) return;
                    }
                    this.formModified = false;
                    
                    this.activeFormId = responseFormId || this.activeFormId;
                    await this.fetchFormDefinition();
                    this.responseId = newResponseId;
                    await this.fetchPastResponse();
                    this.page = 1;
                    this.showHistoricResponses = false;
                    notify.primary('Previous response loaded', 'top');

                } catch (error) {
                    console.error('Error loading previous response:', error);
                    notify.error('Failed to load previous response', 'top');
                } finally {
                    this.loadingHistory = false;
                }
            },
            /**
             * Called after deleting the current form from the past responses modal.
             * Closes modal and emits exit event.
             */
            handleDeleteCurrentForm() {
                this.showHistoricResponses = false;
                this.$emit('exit');
            },
            /**
             * Marks the form as incomplete to allow further editing.
             */
            async reopenForm() {
                try {
                    this.stopAutoSave();
                    this.reopening = true;
                    const status = this.getStatus() ?? 'incomplete';
                    if (this.responseId) {
                        await api.forms.updateDynamicForm(this.activeFormId, this.persistenceSubject, this.responseId, this.response, false, status, this.responseMetadata?.revision);
                        await this.fetchPastResponse();
                        if (this.responseMetadata) {
                            this.responseMetadata.completed_on = null;
                            if (!this.structure.requiresApproval) this.responseMetadata.status = status;
                        }
                        notify.primary('Form marked as incomplete. The form can now be edited.', 'top');
                        this.startAutoSave();
                    }
                } catch (err) {
                    console.error("Error marking form as incomplete:", err);
                    notify.error("Failed to mark form as incomplete. Please try again later.", 'top');
                } finally {
                    this.reopening = false;
                }
            },
            /**
             * Submit the form with the given status text.
             * @param complete flag for if form is to be marked as complete
             * @param isAutoSave flag for if the request is an auto-save
             */
            async submitForm(complete=false, isAutoSave=false) {
                if (this.submitting || this.autosaving) return false;
                if (this.standaloneMode && (!complete || isAutoSave)) return false;
                if (isAutoSave && (!this.formModified || Object.keys(this.response).length === 0 || this.isReadOnly)) return true;
                const savedAnswers = JSON.stringify(this.response);
                try {

                    if (!isAutoSave) this.submitting = true;
                    else this.autosaving = true;

                    if (this.externalMode) {
                        await api.forms.submitExternalFormInvitation(this.externalToken, this.response, this.structure.id);
                        this.formModified = false;
                        this.$emit('form-submitted');
                        return true;
                    }

                    const status = this.getStatus() ?? (complete ? 'complete' : 'incomplete');
                    if (this.publicMode) {
                        const response = await api.publicKoTatou.submitForm(this.response);
                        this.responseId = response.data.id;
                        notify.primary("Form submitted successfully", 'top');
                    } else if (this.responseId) {
                        const saved = await api.forms.updateDynamicForm(
                            this.activeFormId,
                            this.persistenceSubject,
                            this.responseId,
                            this.response,
                            complete,
                            status,
                            this.responseMetadata?.revision
                        );
                        this.responseMetadata = { ...this.responseMetadata, revision: saved.data.revision, status: saved.data.status };
                        if (!isAutoSave) {
                            notify.primary(`${complete ? "Form submitted successfully" : "Form saved successfully"}`, 'top');
                        }
                    } else {
                        const response = await api.forms.submitDynamicForm(
                            this.activeFormId,
                            this.subject,
                            this.response,
                            complete,
                            status
                        );
                        this.responseId = response.data.id;
                        await this.fetchPastResponse(isAutoSave);
                        if (!isAutoSave) {
                            notify.primary(`${complete ? "Form submitted successfully" : "Form saved successfully"}`, 'top');
                        }
                    }
                    
                    // Keep edits made while an autosave was in flight marked as unsaved.
                    this.formModified = JSON.stringify(this.response) !== savedAnswers;
                    if (this.structure.requiresApproval && !isAutoSave) await this.fetchPastResponse(true);
                    if (complete) this.$emit('form-submitted');
                    return true;
                } catch (err) {
                    console.error("Error submitting form:", err);
                    if (this.publicMode && err.response?.status === 401) {
                        this.$emit(
                            'access-expired',
                            err.response?.data?.message || 'Your form access has expired. Please enter the PIN again.'
                        );
                    }
                    if (err.response?.status === 409) {
                        this.stopAutoSave();
                        this.formErrors = [err.response?.data?.message || 'This form has changed. Reload it before continuing.'];
                    }
                    if (!isAutoSave || err.response?.status === 409) {
                        this.$q.notify({
                            type: 'warning',
                            message: err.response?.data?.message || 'An error occurred while submitting the form. Please try again later.',
                            position: 'top',
                        });
                    }
                    return false;
                } finally {
                    this.submitting = false;
                    this.autosaving = false;
                }
            },
            capitalise(string) {
                if (typeof string !== 'string') return '';
                return string
                    .replace(/_/g, ' ')
                    .split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(' ');
            },
            formatDate(dateStr) {
                if (!dateStr) return '-';
                const date = new Date(dateStr);
                return isNaN(date.getTime()) ? '-' : date.toLocaleString('en-nz');
            },
            /**
             * Hacky way of storing status by taking the first field marked as is_status and using its value if present.
             */
            getStatus() {
                for (const [field, value] of Object.entries(this.response)) {
                    for (const page of this.pagesList) {
                        const statusField = page.fields?.find(fld => fld.id === field && fld.isStatus);
                        if (statusField) {
                            return value?.value;
                        }
                    }
                }
                return null;
            }
        },
        computed: {
            approvedByName() {
                return this.responseMetadata?.status === 'approved' ? this.responseMetadata.approved_by_name || null : null;
            },
            responseStatusText() {
                return this.approvedByName ? `Approved by ${this.approvedByName}` : this.capitalise(this.responseMetadata?.status);
            },
            externalMode() {
                return !!this.externalToken;
            },
            standaloneMode() {
                return this.externalMode || this.publicMode;
            },
            subjectName() {
                return this.subject?.name || "-";
            },
            totalPages() {
                return this.pagesList.length;
            },
            pagesList() {
                if (!this.structure) return [];
                if (Array.isArray(this.structure.pages) && this.structure.pages.length) {
                    return this.structure.pages;
                }
                return [this.structure];
            },
            isReadOnly() {
                return this.responseMetadata?.completed_on !== undefined && this.responseMetadata?.completed_on !== null;
            },
            persistenceSubject() {
                if (!this.responseId || !this.responseMetadata) return this.subject;
                return subjectWithClientEngagement(
                    subjectWithoutClientEngagement(this.subject),
                    this.responseMetadata.client_engagement_id
                );
            },
            // Anonymous responses stand alone and are not Te Kohinga people.
            isTemporaryClient() {
                return !this.standaloneMode
                    && ['tk_person', 'legacy_temporary'].includes(this.subject?.source);
            }
        },
    };
</script>

<style scoped>
.tk-client-badge {
    padding: 5px 9px;
    margin-bottom: 3px;
    border-radius: 999px;
    font-size: 0.78rem;
    line-height: 1.1;
    vertical-align: middle;
}
</style>
